import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = [
  '#16a34a',
  '#dc2626',
  '#d97706',
  '#0ea5e9',
  '#64748b',
  '#8b5cf6',
];

const COMPANY_LOGO =
  'https://z-cdn-media.chatglm.cn/files/833f76ac-d356-4ddc-999d-cb7ff101a341.jpeg?auth_key=1881342992-d617d47404de42288c9307279d8eb98c-0-cf26d53626244e8c7b1dec63b8da006e';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] =
    useState('all');
  const [isExporting, setIsExporting] =
    useState(false);

  // ============================================================
  // LOAD BRANCHES
  // ============================================================

  useEffect(() => {
    api
      .get('/branches?isActive=true')
      .then((res) =>
        setBranches(
          res.data.data || []
        )
      )
      .catch((e) =>
        console.error(
          'Failed to load branches',
          e
        )
      );
  }, []);

  // ============================================================
  // LOAD DASHBOARD
  // ============================================================

  useEffect(() => {
    fetchOverview();
  }, [selectedBranch]);

  const fetchOverview = async () => {
    setLoading(true);

    try {
      const query =
        selectedBranch !== 'all'
          ? `?branch=${selectedBranch}`
          : '';

      const res =
        await api.get(
          `/dashboard/overview${query}`
        );

      setData(
        res.data.data
      );
    } catch (e) {
      console.error(
        'Failed to load dashboard',
        e
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // EXPORT
  // ============================================================

  const handleExport = () => {
    if (!data) return;

    setIsExporting(true);

    try {
      const rows = [];

      const mf =
        data.moneyFlow || {};

      const rates =
        data.rates || {};

      const ti =
        data.tieredInterest || {};

      const onboarding =
        data.onboardingFees || {};

      const branchName =
        selectedBranch === 'all'
          ? 'All Branches'
          : branches.find(
              (b) =>
                b._id === selectedBranch
            )?.name ||
            'Selected Branch';

      rows.push([
        'FLEXBOOST DASHBOARD REPORT',
      ]);

      rows.push([
        'Branch',
        branchName,
      ]);

      rows.push([
        'Generated On',
        new Date().toLocaleString(),
      ]);

      rows.push([]);

      // ========================================================
      // MONEY FLOW
      // ========================================================

      rows.push([
        '═══ MONEY FLOW ═══',
      ]);

      rows.push([
        'Total Disbursed (All Time)',
        mf.totalDisbursed || 0,
      ]);

      rows.push([
        'Total Net Disbursed',
        mf.totalNetDisbursed || 0,
      ]);

      rows.push([
        'Total Loan Repayments',
        mf.totalRepaid || 0,
      ]);

      rows.push([
        'Onboarding Fees Collected',
        mf.onboardingFeesTotal || 0,
      ]);

      rows.push([
        'Total Cash Received',
        mf.totalCashReceived || 0,
      ]);

      rows.push([
        'Repayment Rate',
        `${mf.repaymentRate || 0}%`,
      ]);

      rows.push([
        'Total Outstanding',
        mf.totalOutstanding || 0,
      ]);

      rows.push([
        'Loan Revenue',
        mf.loanRevenueEarned || 0,
      ]);

      rows.push([
        'Total Revenue',
        mf.totalRevenueEarned || 0,
      ]);

      rows.push([
        'Net Cash Position',
        mf.netPosition || 0,
      ]);

      rows.push([]);

      // ========================================================
      // ONBOARDING FEES
      // ========================================================

      rows.push([
        '═══ ONBOARDING FEES ═══',
      ]);

      rows.push([
        'All-Time Onboarding Fees',
        onboarding.allTime?.total || 0,
        `${onboarding.allTime?.count || 0} payments`,
      ]);

      rows.push([
        "Today's Onboarding Fees",
        onboarding.today?.total || 0,
        `${onboarding.today?.count || 0} payments`,
      ]);

      rows.push([
        "This Month's Onboarding Fees",
        onboarding.thisMonth?.total || 0,
        `${onboarding.thisMonth?.count || 0} payments`,
      ]);

      rows.push([
        "Last Month's Onboarding Fees",
        onboarding.lastMonth?.total || 0,
        `${onboarding.lastMonth?.count || 0} payments`,
      ]);

      rows.push([
        'Onboarding Fee Growth',
        `${onboarding.growth || 0}%`,
      ]);

      rows.push([]);

      // ========================================================
      // ONBOARDING PAYMENT METHODS
      // ========================================================

      rows.push([
        'ONBOARDING PAYMENT METHODS',
      ]);

      Object.entries(
        onboarding.sources || {}
      ).forEach(
        ([key, value]) => {
          rows.push([
            key === 'stk'
              ? 'STK Push'
              : key === 'mpesa_code'
                ? 'M-Pesa Code'
                : key,
            value.total || 0,
            `${value.count || 0} payments`,
          ]);
        }
      );

      rows.push([]);

      // ========================================================
      // REPAYMENT BREAKDOWN
      // ========================================================

      rows.push([
        'REPAYMENT BREAKDOWN',
      ]);

      rows.push([
        'Principal Repaid',
        mf.totalPrincipalRepaid || 0,
      ]);

      rows.push([
        'Interest Repaid',
        mf.totalInterestRepaid || 0,
      ]);

      rows.push([
        'Penalty Repaid',
        mf.totalPenaltyRepaid || 0,
      ]);

      rows.push([]);

      // ========================================================
      // PERFORMANCE
      // ========================================================

      rows.push([
        'PERFORMANCE RATES',
      ]);

      rows.push([
        'Collection Rate',
        `${rates.collectionRate || 0}%`,
      ]);

      rows.push([
        'PAR Rate',
        `${rates.parRate || 0}%`,
      ]);

      rows.push([
        'Loan Collection Growth',
        `${rates.collectionGrowth || 0}%`,
      ]);

      rows.push([
        'Onboarding Fee Growth',
        `${rates.onboardingFeeGrowth || 0}%`,
      ]);

      rows.push([]);

      // ========================================================
      // TIERED INTEREST
      // ========================================================

      rows.push([
        '═══ TIERED INTEREST METRICS ═══',
      ]);

      rows.push([
        'Avg Current Interest Rate',
        `${ti.avgCurrentRate || 0}%`,
      ]);

      rows.push([
        'Loans in Penalty Phase',
        ti.penaltyPhaseCount || 0,
      ]);

      rows.push([
        'Penalty Phase Outstanding',
        ti.penaltyPhaseOutstanding || 0,
      ]);

      rows.push([
        'Avg Interest Rate Collected',
        `${ti.avgInterestRateCollected || 0}%`,
      ]);

      rows.push([
        'Avg Days to Repay',
        ti.avgDaysToRepay || 0,
      ]);

      rows.push([]);

      // ========================================================
      // TODAY
      // ========================================================

      rows.push([
        'TODAY',
      ]);

      rows.push([
        "Today's Repayments",
        data.todayRepayments?.total || 0,
        `${data.todayRepayments?.count || 0} txns`,
      ]);

      rows.push([
        "Today's Disbursements",
        data.todayDisbursements?.totalPrincipal || 0,
        `${data.todayDisbursements?.count || 0} loans`,
      ]);

      rows.push([
        "Today's Onboarding Fees",
        onboarding.today?.total || 0,
        `${onboarding.today?.count || 0} payments`,
      ]);

      rows.push([]);

      // ========================================================
      // MONTH
      // ========================================================

      rows.push([
        'THIS MONTH',
      ]);

      rows.push([
        'Month Repayments',
        data.monthRepayments?.total || 0,
      ]);

      rows.push([
        'Month Disbursements',
        data.monthDisbursements?.totalPrincipal || 0,
      ]);

      rows.push([
        "Month's Onboarding Fees",
        onboarding.thisMonth?.total || 0,
      ]);

      rows.push([
        'Loans Cleared',
        data.clearedThisMonth?.count || 0,
      ]);

      rows.push([]);

      // ========================================================
      // PORTFOLIO
      // ========================================================

      rows.push([
        'PORTFOLIO',
      ]);

      const p =
        data.portfolio || {};

      [
        'active',
        'overdue',
        'defaulted',
        'closed',
      ].forEach(
        (status) => {
          if (p[status]) {
            rows.push([
              status,
              p[status].count,
              p[status].totalOutstanding,
            ]);
          }
        }
      );

      rows.push([
        'Awaiting Disbursement',
        data.awaitingDisbursement
          ?.count || 0,
        data.awaitingDisbursement
          ?.total || 0,
      ]);

      // ========================================================
      // SAFE CSV ESCAPING
      // ========================================================

      const escapeCSV = (value) => {
        const stringValue =
          value === null ||
          value === undefined
            ? ''
            : String(value);

        if (
          stringValue.includes(',') ||
          stringValue.includes('"') ||
          stringValue.includes('\n')
        ) {
          return `"${stringValue.replace(
            /"/g,
            '""'
          )}"`;
        }

        return stringValue;
      };

      const csv =
        rows
          .map((row) =>
            row
              .map(escapeCSV)
              .join(',')
          )
          .join('\n');

      const blob =
        new Blob(
          [csv],
          {
            type: 'text/csv;charset=utf-8;',
          }
        );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement('a');

      link.href = url;

      link.download =
        `dashboard_report_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        'Export failed',
        err
      );
    } finally {
      setIsExporting(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (
    loading ||
    !data
  ) {
    return (
      <div className="spinner"></div>
    );
  }

  // ============================================================
  // DATA SHORTCUTS
  // ============================================================

  const mf =
    data.moneyFlow || {};

  const rates =
    data.rates || {};

  const p =
    data.portfolio || {};

  const alp =
    data.activeLoanPortfolio || {};

  const ti =
    data.tieredInterest || {};

  const onboarding =
    data.onboardingFees || {};

  // ============================================================
  // OUTSTANDING CALCULATIONS
  // ============================================================

  const totalOutstandingPrincipal =
    (p.active?.outstandingPrincipal || 0) +
    (p.overdue?.outstandingPrincipal || 0) +
    (p.defaulted?.outstandingPrincipal || 0);

  const totalOutstandingInterest =
    (p.active?.outstandingInterest || 0) +
    (p.overdue?.outstandingInterest || 0) +
    (p.defaulted?.outstandingInterest || 0);

  const totalOutstandingPenalty =
    (p.active?.outstandingPenalty || 0) +
    (p.overdue?.outstandingPenalty || 0) +
    (p.defaulted?.outstandingPenalty || 0);

  const calculatedTotalOutstanding =
    totalOutstandingPrincipal +
    totalOutstandingInterest +
    totalOutstandingPenalty;

  const nonPerformingOutstanding =
    (p.overdue?.totalOutstanding || 0) +
    (p.defaulted?.totalOutstanding || 0);

  const nonPerformingCount =
    (p.overdue?.count || 0) +
    (p.defaulted?.count || 0);

  // ============================================================
  // CHART DATA
  // ============================================================

  const portfolioChartData =
    Object.entries(p)
      .filter(
        ([key]) =>
          key !== 'totalDues'
      )
      .map(
        ([key, val]) => ({
          name: key,
          count:
            val?.count || 0,
          amount:
            val?.totalOutstanding || 0,
        })
      );

  // ============================================================
  // MONEY FLOW CHART
  //
  // Includes onboarding fees as a separate cash inflow.
  // ============================================================

  const moneyFlowChartData = [
    {
      name: 'Disbursed',
      value:
        mf.totalDisbursed || 0,
      color: '#3b82f6',
    },

    {
      name: 'Loan Repaid',
      value:
        mf.totalRepaid || 0,
      color: '#059669',
    },

    {
      name: 'Onboarding Fees',
      value:
        mf.onboardingFeesTotal || 0,
      color: '#8b5cf6',
    },

    {
      name: 'Outstanding',
      value:
        mf.totalOutstanding || 0,
      color: '#dc2626',
    },
  ];

  // ============================================================
  // REPAYMENT BREAKDOWN
  // ============================================================

  const repaymentBreakdownData = [
    {
      name: 'Principal',
      value:
        mf.totalPrincipalRepaid || 0,
      color: '#3b82f6',
    },

    {
      name: 'Interest',
      value:
        mf.totalInterestRepaid || 0,
      color: '#f59e0b',
    },

    {
      name: 'Penalty',
      value:
        mf.totalPenaltyRepaid || 0,
      color: '#ef4444',
    },
  ].filter(
    (d) => d.value > 0
  );

  // ============================================================
  // OVERDUE CHART
  // ============================================================

  const overdueBucketData =
    Object.entries(
      data.overdueBuckets || {}
    ).map(
      ([key, val]) => {
        const labels = {
          '0': '1-15d',
          '16': '16-30d',
          '31': '31-90d',
          '91': '91+d',
        };

        return {
          name:
            labels[key] || key,

          count:
            val.count || 0,

          amount:
            val.totalOutstanding || 0,
        };
      }
    );

  // ============================================================
  // REPAYMENT SOURCES
  // ============================================================

  const sourceData =
    Object.entries(
      data.repaymentSources || {}
    ).map(
      ([key, val]) => ({
        name:
          key === 'c2b-auto'
            ? 'M-Pesa Auto'
            : key === 'counter'
              ? 'Counter'
              : key === 'manual'
                ? 'Manual'
                : key === 'bank-transfer'
                  ? 'Bank'
                  : key,

        value:
          val.total || 0,

        count:
          val.count || 0,
      })
    );

  // ============================================================
  // ONBOARDING PAYMENT SOURCES
  // ============================================================

  const onboardingSourceData =
    Object.entries(
      onboarding.sources || {}
    ).map(
      ([key, val]) => ({
        key,

        name:
          key === 'stk'
            ? 'M-Pesa STK Push'
            : key === 'mpesa_code'
              ? 'M-Pesa Transaction Code'
              : key,

        value:
          val.total || 0,

        count:
          val.count || 0,
      })
    );

  // ============================================================
  // INTEREST RATE DISTRIBUTION
  // ============================================================

  const rateDistributionData =
    ti.rateDistribution
      ? [
          {
            name:
              '14% (Days 1-14)',

            value:
              ti.rateDistribution
                .baseRate
                ?.count || 0,

            amount:
              ti.rateDistribution
                .baseRate
                ?.principal || 0,

            color:
              '#059669',
          },

          {
            name:
              '15-29% (Days 15-30)',

            value:
              ti.rateDistribution
                .incrementing
                ?.count || 0,

            amount:
              ti.rateDistribution
                .incrementing
                ?.principal || 0,

            color:
              '#f59e0b',
          },

          {
            name:
              '30% (Day 31+)',

            value:
              ti.rateDistribution
                .penaltyCap
                ?.count || 0,

            amount:
              ti.rateDistribution
                .penaltyCap
                ?.principal || 0,

            color:
              '#dc2626',
          },
        ].filter(
          (d) =>
            d.value > 0
        )
      : [];

  // ============================================================
  // STYLES
  // ============================================================

  const statCard = (
    borderColor,
    bg
  ) => ({
    borderLeft:
      `4px solid ${borderColor}`,

    background:
      bg || '#fff',

    padding:
      '16px 20px',

    borderRadius:
      '10px',

    boxShadow:
      '0 1px 3px rgba(0,0,0,0.06)',
  });

  const ml = {
    fontSize: '11px',
    color: '#64748b',
    marginBottom: '4px',
    fontWeight: 600,
    textTransform:
      'uppercase',
    letterSpacing:
      '0.05em',
  };

  const mv = {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1.2,
  };

  const ms = {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
  };

  const msSmall = {
    fontSize: '10px',
    color: '#94a3b8',
    marginTop: '2px',
  };

  const growthBadge =
    (value) => {
      if (value > 0) {
        return (
          <span
            style={{
              color: '#059669',
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            ▲{' '}
            {Number(
              value
            ).toFixed(1)}
            %
          </span>
        );
      }

      if (value < 0) {
        return (
          <span
            style={{
              color: '#dc2626',
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            ▼{' '}
            {Math.abs(
              Number(value)
            ).toFixed(1)}
            %
          </span>
        );
      }

      return (
        <span
          style={{
            color: '#94a3b8',
            fontSize: 12,
          }}
        >
          — 0%
        </span>
      );
    };

  const rateBadge =
    (rate) => {
      if (rate <= 14) {
        return {
          bg: '#d1fae5',
          color: '#065f46',
          label:
            'Base Rate',
        };
      }

      if (rate < 30) {
        return {
          bg: '#fef3c7',
          color: '#92400e',
          label:
            'Incrementing',
        };
      }

      return {
        bg: '#fee2e2',
        color: '#991b1b',
        label:
          'Penalty Cap',
      };
    };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div>

      {/* ========================================================
          HEADER
          ======================================================== */}

      <div className="flex justify-between items-center mb-3">

        <div className="flex items-center gap-3">

          <img
            src={COMPANY_LOGO}
            alt="FlexBoost"
            style={{
              height: '48px',
              borderRadius: '6px',
            }}
          />

          <div>
            <h2
              style={{
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Dashboard
            </h2>

            <span
              style={{
                fontSize:
                  '0.75rem',
                color:
                  '#64748b',
              }}
            >
              FlexBoost International Ltd
            </span>
          </div>

        </div>

        <div className="flex gap-2">

          <select
            className="form-control"
            style={{
              width: '200px',
            }}
            value={
              selectedBranch
            }
            onChange={(e) =>
              setSelectedBranch(
                e.target.value
              )
            }
          >
            <option value="all">
              All Branches
            </option>

            {branches.map(
              (b) => (
                <option
                  key={b._id}
                  value={b._id}
                >
                  {b.name}
                </option>
              )
            )}
          </select>

          <button
            className="btn btn-secondary"
            onClick={
              handleExport
            }
            disabled={
              isExporting
            }
          >
            {isExporting
              ? 'Exporting...'
              : '📥 Export'}
          </button>

        </div>
      </div>

      {/* ========================================================
          ROW 1
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(4, 1fr)',
        }}
      >

        {/* TOTAL DISBURSED */}

        <div
          style={statCard(
            '#3b82f6',
            'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
          )}
        >
          <div style={ml}>
            💰 Total Disbursed
          </div>

          <div
            style={{
              ...mv,
              color:
                '#1e40af',
            }}
          >
            {formatCurrency(
              mf.totalDisbursed
            )}
          </div>

          <div style={ms}>
            Net to clients:{' '}
            {formatCurrency(
              mf.totalNetDisbursed
            )}
          </div>
        </div>

        {/* TOTAL LOAN REPAYMENTS */}

        <div
          style={statCard(
            '#059669',
            'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          )}
        >
          <div style={ml}>
            ✅ Total Loan Repayments
          </div>

          <div
            style={{
              ...mv,
              color:
                '#065f46',
            }}
          >
            {formatCurrency(
              mf.totalRepaid
            )}
          </div>

          <div style={ms}>
            P:{' '}
            {formatCurrency(
              mf.totalPrincipalRepaid
            )}{' '}
            | I:{' '}
            {formatCurrency(
              mf.totalInterestRepaid
            )}{' '}
            | Pen:{' '}
            {formatCurrency(
              mf.totalPenaltyRepaid
            )}
          </div>

          <div
            style={{
              marginTop: 6,
              padding:
                '4px 8px',
              borderRadius: 4,

              background:
                mf.repaymentRate >= 80
                  ? '#d1fae5'
                  : mf.repaymentRate >= 50
                    ? '#fef3c7'
                    : '#fee2e2',

              fontSize: 11,
              fontWeight: 700,
              display:
                'inline-block',

              color:
                mf.repaymentRate >= 80
                  ? '#065f46'
                  : mf.repaymentRate >= 50
                    ? '#92400e'
                    : '#991b1b',
            }}
          >
            Repayment Rate:{' '}
            {mf.repaymentRate}%
          </div>
        </div>

        {/* ONBOARDING FEES */}

        <div
          style={statCard(
            '#8b5cf6',
            'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)'
          )}
        >
          <div style={ml}>
            🧾 Onboarding Fees
          </div>

          <div
            style={{
              ...mv,
              color:
                '#6d28d9',
            }}
          >
            {formatCurrency(
              mf.onboardingFeesTotal
            )}
          </div>

          <div style={ms}>
            {mf.onboardingFeesCount ||
              0}{' '}
            confirmed payment(s)
          </div>

          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              fontWeight: 600,
              color:
                '#7c3aed',
            }}
          >
            This month:{' '}
            {formatCurrency(
              onboarding.thisMonth
                ?.total || 0
            )}
          </div>
        </div>

        {/* TOTAL REVENUE */}

        <div
          style={statCard(
            '#f59e0b',
            'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
          )}
        >
          <div style={ml}>
            📈 Total Revenue
          </div>

          <div
            style={{
              ...mv,
              color:
                '#92400e',
            }}
          >
            {formatCurrency(
              mf.totalRevenueEarned
            )}
          </div>

          <div style={ms}>
            Loan revenue:{' '}
            {formatCurrency(
              mf.loanRevenueEarned
            )}
          </div>

          <div
            style={{
              ...ms,
              marginTop: 2,
            }}
          >
            Onboarding:{' '}
            {formatCurrency(
              mf.onboardingFeesTotal
            )}
          </div>
        </div>

      </div>

      {/* ========================================================
          ROW 2
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(4, 1fr)',
        }}
      >

        {/* REPAYMENT RATE */}

        <div
          style={statCard(
            '#059669'
          )}
        >
          <div style={ml}>
            Repayment Rate
          </div>

          <div
            style={{
              ...mv,
              color:
                mf.repaymentRate >= 80
                  ? '#059669'
                  : mf.repaymentRate >= 50
                    ? '#d97706'
                    : '#dc2626',
            }}
          >
            {mf.repaymentRate}%
          </div>

          <div
            style={{
              marginTop: 8,
              height: 8,
              background:
                '#e2e8f0',
              borderRadius: 4,
              overflow:
                'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(
                  mf.repaymentRate ||
                    0,
                  100
                )}%`,
                height: '100%',
                background:
                  mf.repaymentRate >= 80
                    ? '#059669'
                    : mf.repaymentRate >= 50
                      ? '#f59e0b'
                      : '#dc2626',
                borderRadius: 4,
                transition:
                  'width 0.5s ease',
              }}
            />
          </div>

          <div style={ms}>
            Loan repayments ÷
            loan principal
            disbursed
          </div>
        </div>

        {/* PAR */}

        <div
          style={statCard(
            '#dc2626'
          )}
        >
          <div style={ml}>
            Portfolio at Risk
          </div>

          <div
            style={{
              ...mv,
              color:
                rates.parRate <= 5
                  ? '#059669'
                  : rates.parRate <= 15
                    ? '#d97706'
                    : '#dc2626',
            }}
          >
            {rates.parRate}%
          </div>

          <div
            style={{
              marginTop: 8,
              height: 8,
              background:
                '#e2e8f0',
              borderRadius: 4,
              overflow:
                'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(
                  rates.parRate ||
                    0,
                  100
                )}%`,
                height: '100%',
                background:
                  rates.parRate <= 5
                    ? '#059669'
                    : rates.parRate <= 15
                      ? '#f59e0b'
                      : '#dc2626',
                borderRadius: 4,
              }}
            />
          </div>

          <div style={ms}>
            Overdue + Defaulted
          </div>
        </div>

        {/* LOAN COLLECTION GROWTH */}

        <div
          style={statCard(
            '#8b5cf6'
          )}
        >
          <div style={ml}>
            Loan Collection Growth
          </div>

          <div
            style={{
              ...mv,
            }}
          >
            {growthBadge(
              rates.collectionGrowth
            )}
          </div>

          <div style={ms}>
            This month vs last month
          </div>
        </div>

        {/* NET POSITION */}

        <div
          style={statCard(
            mf.netPosition >= 0
              ? '#059669'
              : '#dc2626'
          )}
        >
          <div style={ml}>
            Net Cash Position
          </div>

          <div
            style={{
              ...mv,
              color:
                mf.netPosition >= 0
                  ? '#059669'
                  : '#dc2626',
            }}
          >
            {mf.netPosition >= 0
              ? '+'
              : ''}
            {formatCurrency(
              mf.netPosition
            )}
          </div>

          <div style={ms}>
            Repayments + onboarding
            fees − disbursements
          </div>
        </div>

      </div>

      {/* ========================================================
          ONBOARDING FEE SUMMARY
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(4, 1fr)',
        }}
      >

        {/* TODAY */}

        <div
          style={statCard(
            '#8b5cf6',
            '#faf5ff'
          )}
        >
          <div style={ml}>
            🧾 Today's Onboarding Fees
          </div>

          <div
            style={{
              ...mv,
              color:
                '#7c3aed',
            }}
          >
            {formatCurrency(
              onboarding.today
                ?.total || 0
            )}
          </div>

          <div style={ms}>
            {onboarding.today
              ?.count || 0}{' '}
            confirmed payments
          </div>
        </div>

        {/* MONTH */}

        <div
          style={statCard(
            '#6366f1',
            '#eef2ff'
          )}
        >
          <div style={ml}>
            📅 This Month
          </div>

          <div
            style={{
              ...mv,
              color:
                '#4338ca',
            }}
          >
            {formatCurrency(
              onboarding.thisMonth
                ?.total || 0
            )}
          </div>

          <div style={ms}>
            {onboarding.thisMonth
              ?.count || 0}{' '}
            payments
          </div>
        </div>

        {/* GROWTH */}

        <div
          style={statCard(
            '#0ea5e9'
          )}
        >
          <div style={ml}>
            Onboarding Fee Growth
          </div>

          <div
            style={{
              ...mv,
            }}
          >
            {growthBadge(
              onboarding.growth ||
                0
            )}
          </div>

          <div style={ms}>
            This month vs last month
          </div>
        </div>

        {/* COUNT */}

        <div
          style={statCard(
            '#059669'
          )}
        >
          <div style={ml}>
            Confirmed Onboarding Payments
          </div>

          <div
            style={{
              ...mv,
              color:
                '#059669',
            }}
          >
            {mf.onboardingFeesCount ||
              0}
          </div>

          <div style={ms}>
            Paid onboarding fees
          </div>
        </div>

      </div>

      {/* ========================================================
          TODAY + MONTH LOAN PERFORMANCE
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(4, 1fr)',
        }}
      >

        <div
          style={statCard(
            '#10b981'
          )}
        >
          <div style={ml}>
            Today's Repayments
          </div>

          <div
            style={{
              ...mv,
              color:
                '#059669',
            }}
          >
            {formatCurrency(
              data.todayRepayments
                ?.total || 0
            )}
          </div>

          <div style={ms}>
            {data.todayRepayments
              ?.count || 0}{' '}
            payment(s) |
            P:{' '}
            {formatCurrency(
              data.todayRepayments
                ?.principal || 0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              data.todayRepayments
                ?.interest || 0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#8b5cf6'
          )}
        >
          <div style={ml}>
            Today's Disbursements
          </div>

          <div
            style={{
              ...mv,
              color:
                '#7c3aed',
            }}
          >
            {formatCurrency(
              data.todayDisbursements
                ?.totalPrincipal ||
                0
            )}
          </div>

          <div style={ms}>
            {data.todayDisbursements
              ?.count || 0}{' '}
            loan(s)
          </div>
        </div>

        <div
          style={statCard(
            '#0ea5e9'
          )}
        >
          <div style={ml}>
            This Month's Repayments
          </div>

          <div
            style={{
              ...mv,
              color:
                '#0284c7',
            }}
          >
            {formatCurrency(
              data.monthRepayments
                ?.total || 0
            )}
          </div>

          <div style={ms}>
            P:{' '}
            {formatCurrency(
              data.monthRepayments
                ?.principal || 0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              data.monthRepayments
                ?.interest || 0
            )}{' '}
            |
            Pen:{' '}
            {formatCurrency(
              data.monthRepayments
                ?.penalty || 0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#6366f1'
          )}
        >
          <div style={ml}>
            This Month's Disbursements
          </div>

          <div
            style={{
              ...mv,
              color:
                '#4f46e5',
            }}
          >
            {formatCurrency(
              data.monthDisbursements
                ?.totalPrincipal ||
                0
            )}
          </div>

          <div style={ms}>
            {data.monthDisbursements
              ?.count || 0}{' '}
            loan(s)
          </div>
        </div>

      </div>

      {/* ========================================================
          PORTFOLIO STATUS
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(5, 1fr)',
        }}
      >

        <div
          style={statCard(
            '#16a34a'
          )}
        >
          <div style={ml}>
            Active Loans
          </div>

          <div
            style={{
              ...mv,
              color:
                '#15803d',
            }}
          >
            {p.active
              ?.count || 0}
          </div>

          <div style={ms}>
            {formatCurrency(
              p.active
                ?.totalOutstanding ||
                0
            )}{' '}
            outstanding
          </div>

          <div style={msSmall}>
            P:{' '}
            {formatCurrency(
              p.active
                ?.outstandingPrincipal ||
                0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              p.active
                ?.outstandingInterest ||
                0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#dc2626'
          )}
        >
          <div style={ml}>
            Overdue Loans
          </div>

          <div
            style={{
              ...mv,
              color:
                '#dc2626',
            }}
          >
            {p.overdue
              ?.count || 0}
          </div>

          <div style={ms}>
            {formatCurrency(
              p.overdue
                ?.totalOutstanding ||
                0
            )}
          </div>

          <div style={msSmall}>
            P:{' '}
            {formatCurrency(
              p.overdue
                ?.outstandingPrincipal ||
                0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              p.overdue
                ?.outstandingInterest ||
                0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#991b1b'
          )}
        >
          <div style={ml}>
            Defaulted (90+d)
          </div>

          <div
            style={{
              ...mv,
              color:
                '#991b1b',
            }}
          >
            {p.defaulted
              ?.count || 0}
          </div>

          <div style={ms}>
            {formatCurrency(
              p.defaulted
                ?.totalOutstanding ||
                0
            )}
          </div>

          <div style={msSmall}>
            P:{' '}
            {formatCurrency(
              p.defaulted
                ?.outstandingPrincipal ||
                0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              p.defaulted
                ?.outstandingInterest ||
                0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#f59e0b',
            '#fffbeb'
          )}
        >
          <div style={ml}>
            Awaiting Disbursement
          </div>

          <div
            style={{
              ...mv,
              color:
                '#b45309',
            }}
          >
            {formatCurrency(
              data.awaitingDisbursement
                ?.total || 0
            )}
          </div>

          <div style={ms}>
            {data.awaitingDisbursement
              ?.count || 0}{' '}
            approved
          </div>
        </div>

        <div
          style={statCard(
            '#059669',
            '#ecfdf5'
          )}
        >
          <div style={ml}>
            Loans Cleared
          </div>

          <div
            style={{
              ...mv,
              color:
                '#059669',
            }}
          >
            {data.clearedThisMonth
              ?.count || 0}
          </div>

          <div style={ms}>
            Interest earned:{' '}
            {formatCurrency(
              data.clearedThisMonth
                ?.interest || 0
            )}
          </div>
        </div>

      </div>

      {/* ========================================================
          PERFORMING VS NON-PERFORMING
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(3, 1fr)',
        }}
      >

        <div
          style={statCard(
            '#059669',
            'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          )}
        >
          <div style={ml}>
            ✅ Performing Portfolio
          </div>

          <div
            style={{
              ...mv,
              color:
                '#065f46',
            }}
          >
            {formatCurrency(
              p.active
                ?.totalOutstanding ||
                0
            )}
          </div>

          <div style={ms}>
            {p.active
              ?.count || 0}{' '}
            loans on schedule
          </div>

          <div style={msSmall}>
            P:{' '}
            {formatCurrency(
              p.active
                ?.outstandingPrincipal ||
                0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              p.active
                ?.outstandingInterest ||
                0
            )}
          </div>
        </div>

        <div
          style={statCard(
            '#dc2626',
            'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
          )}
        >
          <div style={ml}>
            ⚠️ Non-Performing
          </div>

          <div
            style={{
              ...mv,
              color:
                '#991b1b',
            }}
          >
            {formatCurrency(
              nonPerformingOutstanding
            )}
          </div>

          <div style={ms}>
            {nonPerformingCount}{' '}
            loans at risk
          </div>

          <div style={msSmall}>
            Overdue:{' '}
            {p.overdue
              ?.count || 0}{' '}
            |
            Defaulted:{' '}
            {p.defaulted
              ?.count || 0}
          </div>
        </div>

        <div
          style={statCard(
            '#3b82f6',
            'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
          )}
        >
          <div style={ml}>
            📊 Active Book
          </div>

          <div
            style={{
              ...mv,
              color:
                '#1e40af',
            }}
          >
            {formatCurrency(
              (alp.principal || 0) +
              (alp.interest || 0) +
              (alp.penalty || 0)
            )}
          </div>

          <div style={ms}>
            {alp.count || 0}{' '}
            loans
          </div>

          <div style={msSmall}>
            P:{' '}
            {formatCurrency(
              alp.principal || 0
            )}{' '}
            |
            I:{' '}
            {formatCurrency(
              alp.interest || 0
            )}{' '}
            |
            Pen:{' '}
            {formatCurrency(
              alp.penalty || 0
            )}
          </div>
        </div>

      </div>

      {/* ========================================================
          ONBOARDING PAYMENT METHODS
          ======================================================== */}

      {onboardingSourceData.length >
        0 && (
        <div
          className="card"
          style={{
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div className="flex justify-between items-center">
            <div>
              <h4
                style={{
                  marginBottom: 4,
                }}
              >
                🧾 Onboarding Fee Collection
              </h4>

              <p
                style={{
                  fontSize: 11,
                  color:
                    '#64748b',
                  margin:
                    '0 0 16px 0',
                }}
              >
                Confirmed onboarding fees
                collected through STK
                Push and M-Pesa transaction
                codes.
              </p>
            </div>

            <div
              style={{
                textAlign:
                  'right',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color:
                    '#64748b',
                  fontWeight:
                    600,
                }}
              >
                TOTAL
              </div>

              <div
                style={{
                  fontSize: 20,
                  fontWeight:
                    700,
                  color:
                    '#7c3aed',
                }}
              >
                {formatCurrency(
                  mf.onboardingFeesTotal ||
                    0
                )}
              </div>
            </div>
          </div>

          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns:
                `repeat(${Math.max(
                  onboardingSourceData.length,
                  1
                )}, 1fr)`,
            }}
          >
            {onboardingSourceData.map(
              (s, i) => {
                const colors = [
                  '#7c3aed',
                  '#059669',
                  '#3b82f6',
                  '#f59e0b',
                ];

                const color =
                  colors[
                    i %
                      colors.length
                  ];

                const pct =
                  mf.onboardingFeesTotal >
                  0
                    ? (
                        (s.value /
                          mf.onboardingFeesTotal) *
                        100
                      ).toFixed(1)
                    : 0;

                return (
                  <div
                    key={s.key}
                    style={{
                      textAlign:
                        'center',
                      padding: 16,
                      borderRadius: 8,
                      background:
                        '#f8fafc',
                      border:
                        `2px solid ${color}20`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 25,
                      }}
                    >
                      {s.key ===
                      'stk'
                        ? '📱'
                        : s.key ===
                            'mpesa_code'
                          ? '🔢'
                          : '💳'}
                    </div>

                    <div
                      style={{
                        fontWeight:
                          700,
                        fontSize: 18,
                        color,
                      }}
                    >
                      {formatCurrency(
                        s.value
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color:
                          '#374151',
                        fontWeight:
                          600,
                      }}
                    >
                      {s.name}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color:
                          '#94a3b8',
                      }}
                    >
                      {s.count}{' '}
                      payments
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        fontWeight:
                          700,
                        color,
                      }}
                    >
                      {pct}% of onboarding
                      fees
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          TIERED INTEREST
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(5, 1fr)',
        }}
      >

        <div
          style={statCard(
            '#0ea5e9'
          )}
        >
          <div style={ml}>
            Avg Current Rate
          </div>

          <div
            style={{
              ...mv,
              color:
                '#0284c7',
            }}
          >
            {ti.avgCurrentRate}%
          </div>

          <div style={ms}>
            Range:{' '}
            {ti.minCurrentRate}% -
            {ti.maxCurrentRate}%
          </div>

          <div
            style={{
              marginTop: 6,
              padding:
                '2px 6px',
              borderRadius: 4,
              background:
                rateBadge(
                  ti.avgCurrentRate
                ).bg,
              fontSize: 10,
              fontWeight: 600,
              display:
                'inline-block',
              color:
                rateBadge(
                  ti.avgCurrentRate
                ).color,
            }}
          >
            {
              rateBadge(
                ti.avgCurrentRate
              ).label
            }
          </div>
        </div>

        <div
          style={statCard(
            '#dc2626',
            ti.penaltyPhaseCount >
              0
              ? '#fef2f2'
              : '#fff'
          )}
        >
          <div style={ml}>
            ⚠️ Penalty Phase
          </div>

          <div
            style={{
              ...mv,
              color:
                ti.penaltyPhaseCount >
                0
                  ? '#dc2626'
                  : '#059669',
            }}
          >
            {ti.penaltyPhaseCount}
          </div>

          <div style={ms}>
            Outstanding:{' '}
            {formatCurrency(
              ti.penaltyPhaseOutstanding
            )}
          </div>

          {ti.penaltyPhaseCount >
            0 && (
            <div
              style={{
                marginTop: 4,
                fontSize: 10,
                color:
                  '#dc2626',
                fontWeight:
                  600,
              }}
            >
              Day 31+ loans
            </div>
          )}
        </div>

        <div
          style={statCard(
            '#059669'
          )}
        >
          <div style={ml}>
            Avg Rate Collected
          </div>

          <div
            style={{
              ...mv,
              color:
                '#059669',
            }}
          >
            {ti.avgInterestRateCollected}%
          </div>

          <div style={ms}>
            From{' '}
            {ti.loansFullyRepaid}{' '}
            closed loans
          </div>
        </div>

        <div
          style={statCard(
            '#8b5cf6'
          )}
        >
          <div style={ml}>
            Avg Days to Repay
          </div>

          <div
            style={{
              ...mv,
              color:
                '#7c3aed',
            }}
          >
            {ti.avgDaysToRepay}
          </div>

          <div style={ms}>
            {ti.avgDaysToRepay <=
            14
              ? 'Base rate'
              : ti.avgDaysToRepay <=
                  30
                ? 'Incrementing'
                : 'Penalty cap'}
          </div>
        </div>

        <div
          style={statCard(
            '#f59e0b',
            '#fffbeb'
          )}
        >
          <div style={ml}>
            Total Interest Collected
          </div>

          <div
            style={{
              ...mv,
              color:
                '#b45309',
            }}
          >
            {formatCurrency(
              ti.totalInterestCollected
            )}
          </div>

          <div style={ms}>
            From fully repaid loans
          </div>
        </div>

      </div>

      {/* ========================================================
          CLIENTS
          ======================================================== */}

      <div
        className="grid gap-2 mb-2"
        style={{
          gridTemplateColumns:
            'repeat(3, 1fr)',
        }}
      >

        <div
          style={statCard(
            '#3b82f6'
          )}
        >
          <div style={ml}>
            Total Clients
          </div>

          <div
            style={{
              ...mv,
            }}
          >
            {data.clientStats
              ?.totalClients ||
              0}
          </div>

          <div style={ms}>
            {data.clientStats
              ?.active || 0}{' '}
            with active loans
          </div>
        </div>

        <div
          style={statCard(
            '#f59e0b'
          )}
        >
          <div style={ml}>
            Pending Approvals
          </div>

          <div
            style={{
              ...mv,
              color:
                '#b45309',
            }}
          >
            {data.clientStats
              ?.pendingApprovals ||
              0}
          </div>

          <div style={ms}>
            Clients pending manager
            review
          </div>
        </div>

        <div
          style={statCard(
            '#8b5cf6'
          )}
        >
          <div style={ml}>
            New Leads
          </div>

          <div
            style={{
              ...mv,
              color:
                '#7c3aed',
            }}
          >
            {data.leads
              ?.new || 0}
          </div>

          <div style={ms}>
            Pending follow up
          </div>
        </div>

      </div>

      {/* ========================================================
          MONEY FLOW CHART
          ======================================================== */}

      <div
        className="grid grid-2 gap-2 mb-2"
      >

        <div
          className="card"
          style={{
            padding: 20,
          }}
        >
          <h4
            style={{
              marginBottom:
                16,
            }}
          >
            Money Flow
          </h4>

          <p
            style={{
              fontSize: 11,
              color:
                '#64748b',
              margin:
                '0 0 16px 0',
            }}
          >
            Loan disbursements,
            loan repayments,
            onboarding fees and
            outstanding portfolio.
          </p>

          <div
            style={{
              height: 280,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  moneyFlowChartData
                }
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                />

                <XAxis
                  dataKey="name"
                />

                <YAxis
                  tickFormatter={(
                    v
                  ) =>
                    `${(
                      v / 1000
                    ).toFixed(
                      0
                    )}K`
                  }
                />

                <Tooltip
                  formatter={(
                    val
                  ) =>
                    formatCurrency(
                      val
                    )
                  }
                />

                <Bar
                  dataKey="value"
                  radius={[
                    6,
                    6,
                    0,
                    0,
                  ]}
                >
                  {moneyFlowChartData.map(
                    (
                      d,
                      i
                    ) => (
                      <Cell
                        key={i}
                        fill={
                          d.color
                        }
                      />
                    )
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* REPAYMENT BREAKDOWN */}

        <div
          className="card"
          style={{
            padding: 20,
          }}
        >
          <h4
            style={{
              marginBottom:
                16,
            }}
          >
            Repayment Breakdown
          </h4>

          <div
            style={{
              height: 280,
            }}
          >
            {repaymentBreakdownData.length >
            0 ? (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={
                      repaymentBreakdownData
                    }
                    cx="50%"
                    cy="50%"
                    outerRadius={
                      100
                    }
                    dataKey="value"
                    nameKey="name"
                    label={({
                      name,
                      percent,
                    }) =>
                      `${name}: ${(
                        percent *
                        100
                      ).toFixed(
                        0
                      )}%`
                    }
                  >
                    {repaymentBreakdownData.map(
                      (
                        d,
                        i
                      ) => (
                        <Cell
                          key={i}
                          fill={
                            d.color
                          }
                        />
                      )
                    )}
                  </Pie>

                  <Tooltip
                    formatter={(
                      val
                    ) =>
                      formatCurrency(
                        val
                      )
                    }
                  />

                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  textAlign:
                    'center',
                  padding: 60,
                  color:
                    '#94a3b8',
                }}
              >
                No repayment data yet
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ========================================================
          PORTFOLIO + OVERDUE
          ======================================================== */}

      <div
        className="grid grid-2 gap-2 mb-2"
      >

        <div
          className="card"
          style={{
            padding: 20,
          }}
        >
          <h4
            style={{
              marginBottom:
                16,
            }}
          >
            Portfolio by Status
          </h4>

          <div
            style={{
              height: 280,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <PieChart>
                <Pie
                  data={
                    portfolioChartData
                  }
                  cx="50%"
                  cy="50%"
                  outerRadius={
                    100
                  }
                  dataKey="count"
                  nameKey="name"
                  label={({
                    name,
                    count,
                  }) =>
                    `${name}: ${count}`
                  }
                >
                  {portfolioChartData.map(
                    (
                      _,
                      i
                    ) => (
                      <Cell
                        key={i}
                        fill={
                          COLORS[
                            i %
                              COLORS.length
                          ]
                        }
                      />
                    )
                  )}
                </Pie>

                <Tooltip />

                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          className="card"
          style={{
            padding: 20,
          }}
        >
          <h4
            style={{
              marginBottom:
                16,
            }}
          >
            Overdue Distribution
          </h4>

          <div
            style={{
              height: 280,
            }}
          >
            {overdueBucketData.length >
            0 ? (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={
                    overdueBucketData
                  }
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="name"
                  />

                  <YAxis />

                  <Tooltip
                    formatter={(
                      val,
                      name
                    ) =>
                      name ===
                      'amount'
                        ? formatCurrency(
                            val
                          )
                        : val
                    }
                  />

                  <Bar
                    dataKey="count"
                    fill="#dc2626"
                    name="Loans"
                    radius={[
                      4,
                      4,
                      0,
                      0,
                    ]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  textAlign:
                    'center',
                  padding: 60,
                  color:
                    '#94a3b8',
                }}
              >
                ✅ No overdue loans!
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ========================================================
          INTEREST RATE DISTRIBUTION
          ======================================================== */}

      {rateDistributionData.length >
        0 && (
        <div
          className="grid grid-2 gap-2 mb-2"
        >

          <div
            className="card"
            style={{
              padding: 20,
            }}
          >
            <h4
              style={{
                marginBottom:
                  8,
              }}
            >
              Interest Rate Distribution
            </h4>

            <p
              style={{
                fontSize: 11,
                color:
                  '#64748b',
                margin:
                  '0 0 16px 0',
              }}
            >
              14% base → +1%/day →
              30% cap
            </p>

            <div
              style={{
                height: 250,
              }}
            >
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={
                      rateDistributionData
                    }
                    cx="50%"
                    cy="50%"
                    outerRadius={
                      90
                    }
                    innerRadius={
                      50
                    }
                    dataKey="value"
                    nameKey="name"
                    label={({
                      name,
                      value,
                    }) =>
                      `${name}: ${value}`
                    }
                  >
                    {rateDistributionData.map(
                      (
                        d,
                        i
                      ) => (
                        <Cell
                          key={i}
                          fill={
                            d.color
                          }
                        />
                      )
                    )}
                  </Pie>

                  <Tooltip
                    formatter={(
                      val,
                      name,
                      props
                    ) => [
                      `${val} loans (${formatCurrency(
                        props.payload
                          .amount
                      )} principal)`,
                      'Count',
                    ]}
                  />

                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            className="card"
            style={{
              padding: 20,
            }}
          >
            <h4
              style={{
                marginBottom:
                  8,
              }}
            >
              Rate Distribution by Principal
            </h4>

            <p
              style={{
                fontSize: 11,
                color:
                  '#64748b',
                margin:
                  '0 0 16px 0',
              }}
            >
              Principal exposure at each
              rate tier.
            </p>

            <div
              style={{
                height: 250,
              }}
            >
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={rateDistributionData.map(
                    (d) => ({
                      ...d,
                      principal:
                        d.amount,
                    })
                  )}
                  layout="vertical"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    type="number"
                    tickFormatter={(
                      v
                    ) =>
                      `${(
                        v / 1000
                      ).toFixed(
                        0
                      )}K`
                    }
                  />

                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{
                      fontSize: 10,
                    }}
                  />

                  <Tooltip
                    formatter={(
                      val
                    ) =>
                      formatCurrency(
                        val
                      )
                    }
                  />

                  <Bar
                    dataKey="principal"
                    name="Principal"
                    radius={[
                      0,
                      4,
                      4,
                      0,
                    ]}
                  >
                    {rateDistributionData.map(
                      (
                        d,
                        i
                      ) => (
                        <Cell
                          key={i}
                          fill={
                            d.color
                          }
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>
      )}

      {/* ========================================================
          REPAYMENT CHANNEL BREAKDOWN
          ======================================================== */}

      {sourceData.length >
        0 && (
        <div
          className="card"
          style={{
            padding: 20,
            marginBottom: 16,
          }}
        >
          <h4
            style={{
              marginBottom:
                12,
            }}
          >
            Repayment Channel Breakdown
          </h4>

          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns:
                `repeat(${sourceData.length}, 1fr)`,
            }}
          >
            {sourceData.map(
              (
                s,
                i
              ) => {
                const colors = [
                  '#059669',
                  '#3b82f6',
                  '#f59e0b',
                  '#8b5cf6',
                  '#dc2626',
                ];

                const icons = {
                  'M-Pesa Auto':
                    '📱',

                  Counter:
                    '🏢',

                  Manual:
                    '📋',

                  Bank:
                    '🏦',
                };

                const pct =
                  mf.totalRepaid >
                  0
                    ? (
                        (s.value /
                          mf.totalRepaid) *
                        100
                      ).toFixed(
                        1
                      )
                    : 0;

                return (
                  <div
                    key={i}
                    style={{
                      textAlign:
                        'center',
                      padding: 16,
                      borderRadius: 8,
                      background:
                        '#f8fafc',
                      border:
                        `2px solid ${
                          colors[
                            i %
                              colors.length
                          ]
                        }20`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 24,
                      }}
                    >
                      {icons[
                        s.name
                      ] ||
                        '💳'}
                    </div>

                    <div
                      style={{
                        fontWeight:
                          700,
                        fontSize: 18,
                        color:
                          colors[
                            i %
                              colors.length
                          ],
                      }}
                    >
                      {formatCurrency(
                        s.value
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color:
                          '#374151',
                        fontWeight:
                          600,
                      }}
                    >
                      {s.name}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color:
                          '#94a3b8',
                      }}
                    >
                      {s.count}{' '}
                      payments
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        fontWeight:
                          700,
                        color:
                          colors[
                            i %
                              colors.length
                          ],
                      }}
                    >
                      {pct}% of total
                      repaid
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          ONBOARDING FEE ACCOUNTING NOTE
          ======================================================== */}

      <div
        className="card"
        style={{
          padding: 16,
          marginBottom: 20,
          background:
            '#f8fafc',
          border:
            '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            display:
              'flex',
            gap: 12,
            alignItems:
              'flex-start',
          }}
        >
          <div
            style={{
              fontSize: 24,
            }}
          >
            ℹ️
          </div>

          <div>
            <div
              style={{
                fontWeight:
                  700,
                color:
                  '#334155',
                marginBottom:
                  4,
              }}
            >
              Dashboard Accounting
            </div>

            <div
              style={{
                fontSize: 12,
                color:
                  '#64748b',
                lineHeight:
                  1.6,
              }}
            >
              Onboarding fees are confirmed
              separately from loan repayments.
              They contribute to total revenue
              and net cash position, but they are
              not included in the loan repayment
              rate or loan repayment totals.
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}